// ── Feature keys ────────────────────────────────────────────
export type FeatureKey =
  | 'allInOne'
  | 'crm'
  | 'aiLeadNurturing'
  | 'satelliteMeasurements'
  | 'estimating'
  | 'jobManagement'
  | 'crewScheduling'
  | 'marketingAutomation'
  | 'weatherTracking'
  | 'quickbooks'
  | 'documentSigning'
  | 'unlimitedUsers'
  | 'aiAgent'
  | 'mobileApp'

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  allInOne: 'All-in-One Platform',
  crm: 'Full CRM & Pipeline',
  aiLeadNurturing: 'AI Lead Nurturing (Pro plan)',
  satelliteMeasurements: 'Satellite Measurements',
  estimating: 'Estimating & Proposals',
  jobManagement: 'Job Management',
  crewScheduling: 'Crew Scheduling & Dispatch',
  marketingAutomation: 'Marketing Automation',
  weatherTracking: 'Weather & Hail Tracking (Pro plan)',
  quickbooks: 'QuickBooks Integration',
  documentSigning: 'Document Signing',
  unlimitedUsers: 'Unlimited Users',
  aiAgent: 'Sierra AI Agent (Pro plan)',
  mobileApp: 'Mobile Access (Phone & Tablet)',
}

export const FEATURE_ORDER: FeatureKey[] = [
  'allInOne',
  'crm',
  'aiLeadNurturing',
  'satelliteMeasurements',
  'estimating',
  'jobManagement',
  'crewScheduling',
  'marketingAutomation',
  'weatherTracking',
  'quickbooks',
  'documentSigning',
  'unlimitedUsers',
  'aiAgent',
  'mobileApp',
]

// BuilderLync has ALL features
export const BUILDERLYNC_FEATURES: Record<FeatureKey, boolean> = {
  allInOne: true,
  crm: true,
  aiLeadNurturing: true,
  satelliteMeasurements: true,
  estimating: true,
  jobManagement: true,
  crewScheduling: true,
  marketingAutomation: true,
  weatherTracking: true,
  quickbooks: true,
  documentSigning: true,
  unlimitedUsers: true,
  aiAgent: true,
  mobileApp: true,
}

// ── Competitor type ─────────────────────────────────────────
export interface Competitor {
  slug: string
  name: string
  company: string
  price: string
  priceNote: string
  tagline: string
  features: Record<FeatureKey, boolean | 'limited'>
  pros: string[]
  cons: string[]
  whySwitch: { title: string; description: string }[]
  faqs: { question: string; answer: string }[]
}

// ── Competitor data ─────────────────────────────────────────
export const COMPETITORS: Record<string, Competitor> = {
  roofr: {
    slug: 'roofr',
    name: 'RoofR',
    company: 'RoofR Inc.',
    price: '$149–$399/mo',
    priceNote: 'Per-tier pricing with limited users on lower plans',
    tagline:
      'An estimating-first platform that lacks full CRM, crew scheduling, and AI automation.',
    features: {
      allInOne: false,
      crm: 'limited',
      aiLeadNurturing: false,
      satelliteMeasurements: 'limited',
      estimating: true,
      jobManagement: 'limited',
      crewScheduling: false,
      marketingAutomation: false,
      weatherTracking: false,
      quickbooks: 'limited',
      documentSigning: true,
      unlimitedUsers: false,
      aiAgent: false,
      mobileApp: true,
    },
    pros: ['Great instant estimator widget', 'Good proposal templates'],
    cons: ['Not a full CRM', 'No crew scheduling', 'No AI agent', 'No marketing automation'],
    whySwitch: [
      {
        title: 'Get a Real CRM',
        description:
          'RoofR offers basic lead tracking but lacks a full CRM pipeline. BuilderLync gives you a complete CRM with AI-powered lead nurturing from day one.',
      },
      {
        title: 'Schedule Crews — Not Just Jobs',
        description:
          'RoofR has no crew scheduling. BuilderLync lets you assign crews, track availability, and dispatch jobs from one dashboard.',
      },
      {
        title: 'AI That Works While You Sleep',
        description:
          'BuilderLync\'s AI agent Sierra handles lead follow-ups, appointment booking, and customer communication 24/7.',
      },
    ],
    faqs: [
      {
        question: 'Can I import my RoofR data into BuilderLync?',
        answer:
          'Yes. You can import your contacts, proposals, and job history from RoofR by CSV on any plan. If you want it done for you, our White Glove migration service is available as a paid add-on.',
      },
      {
        question: 'Does BuilderLync have an instant estimator like RoofR?',
        answer:
          'Yes. BuilderLync includes satellite-powered roof measurements and instant estimate generation, plus full proposal customization with e-signatures.',
      },
      {
        question: 'How does BuilderLync pricing compare to RoofR?',
        answer:
          'BuilderLync starts at $497/mo with unlimited users, and Pro adds Storm Intelligence and the full AI stack at $997/mo. RoofR charges $149–$399/mo but limits features and users per tier, making it more expensive as you scale.',
      },
      {
        question: 'Does BuilderLync integrate with QuickBooks?',
        answer:
          'Yes. BuilderLync has a native QuickBooks integration for syncing invoices, payments, and customer records — something RoofR only partially supports.',
      },
      {
        question: 'Can I try BuilderLync before committing?',
        answer:
          'Absolutely. Book a personalized demo and our team will walk you through the platform with your own data.',
      },
    ],
  },

  acculynx: {
    slug: 'acculynx',
    name: 'AccuLynx',
    company: 'AccuLynx (EagleView)',
    price: '$165–$300/user/mo',
    priceNote: 'Per-user pricing — costs scale linearly with team size',
    tagline:
      'Storm restoration specialist with per-user pricing that scales expensively.',
    features: {
      allInOne: 'limited',
      crm: true,
      aiLeadNurturing: false,
      satelliteMeasurements: true,
      estimating: true,
      jobManagement: true,
      crewScheduling: 'limited',
      marketingAutomation: false,
      weatherTracking: 'limited',
      quickbooks: true,
      documentSigning: true,
      unlimitedUsers: false,
      aiAgent: false,
      mobileApp: true,
    },
    pros: ['Strong storm/insurance workflow', 'Good production tracking'],
    cons: [
      'Very expensive at scale (per-user)',
      'Dated UI',
      'No AI agent',
      'Limited marketing',
    ],
    whySwitch: [
      {
        title: 'Save Thousands Per Month',
        description:
          'AccuLynx charges $165–$300 per user per month. A 10-person team costs $1,650–$3,000/mo. BuilderLync is a flat $497/mo on Starter or $997/mo on Pro, with unlimited users on both.',
      },
      {
        title: 'Modern, Intuitive Interface',
        description:
          'AccuLynx\'s interface feels dated. BuilderLync was designed in 2024 with a modern, mobile-first UI that your team will actually enjoy using.',
      },
      {
        title: 'AI-Powered Automation',
        description:
          'AccuLynx has no AI capabilities. BuilderLync\'s AI agent handles follow-ups, scheduling, and lead nurturing automatically.',
      },
    ],
    faqs: [
      {
        question: 'How much will I save switching from AccuLynx?',
        answer:
          'Most teams save $1,000–$2,500/month switching to BuilderLync. Our unlimited user pricing means you never pay more as your team grows.',
      },
      {
        question: 'Does BuilderLync support storm/insurance workflows?',
        answer:
          'Yes. BuilderLync includes insurance claim tracking and supplement management on every plan. Storm hail tracking with weather integration is part of Storm Intelligence on the Pro plan.',
      },
      {
        question: 'Can I migrate my AccuLynx data?',
        answer:
          'Yes. CSV imports are included on every plan, and our White Glove service, a paid add-on, handles the full migration of your contacts, jobs, documents, and history from AccuLynx.',
      },
      {
        question: 'Does BuilderLync integrate with EagleView?',
        answer:
          'Yes. BuilderLync has native EagleView integration for satellite measurements, plus our own built-in measurement tools.',
      },
      {
        question: 'Is BuilderLync suitable for large roofing companies?',
        answer:
          'Absolutely. With unlimited users, role-based permissions, and multi-location support, BuilderLync scales with your business.',
      },
    ],
  },

  jobnimbus: {
    slug: 'jobnimbus',
    name: 'JobNimbus',
    company: 'JobNimbus LLC.',
    price: '$25–$250/mo',
    priceNote: 'Per-user pricing on higher tiers, limited features on lower',
    tagline:
      'Affordable CRM with Kanban boards but missing AI, measurements, and marketing.',
    features: {
      allInOne: false,
      crm: true,
      aiLeadNurturing: false,
      satelliteMeasurements: false,
      estimating: 'limited',
      jobManagement: true,
      crewScheduling: 'limited',
      marketingAutomation: false,
      weatherTracking: false,
      quickbooks: 'limited',
      documentSigning: true,
      unlimitedUsers: false,
      aiAgent: false,
      mobileApp: true,
    },
    pros: ['Simple Kanban interface', 'Affordable starting price'],
    cons: [
      'Basic estimating',
      'No satellite measurements',
      'No AI',
      'Per-user pricing at scale',
    ],
    whySwitch: [
      {
        title: 'Satellite Measurements Built In',
        description:
          'JobNimbus has no measurement tools. BuilderLync includes satellite roof measurements so you can generate accurate estimates without climbing a roof.',
      },
      {
        title: 'AI Lead Follow-Up',
        description:
          'JobNimbus requires manual follow-ups. BuilderLync\'s AI agent automatically nurtures leads via text, email, and phone — 24/7.',
      },
      {
        title: 'Marketing Automation Included',
        description:
          'BuilderLync includes email drips, review requests, and referral campaigns — features that JobNimbus doesn\'t offer at all.',
      },
    ],
    faqs: [
      {
        question: 'Is BuilderLync more expensive than JobNimbus?',
        answer:
          'BuilderLync starts at $497/mo with unlimited users on every plan, and the full AI stack is included on Pro at $997/mo. JobNimbus starts low but costs more as you add users and need integrations — many teams end up paying more for less.',
      },
      {
        question: 'Does BuilderLync have Kanban boards like JobNimbus?',
        answer:
          'Yes. BuilderLync features customizable pipeline boards, plus timeline, calendar, and map views that JobNimbus doesn\'t offer.',
      },
      {
        question: 'Can I import data from JobNimbus?',
        answer:
          'Yes. CSV imports are included on every plan. With our White Glove service, a paid add-on, our team handles the full data migration from JobNimbus including contacts, jobs, notes, and documents.',
      },
      {
        question: 'Does BuilderLync work on mobile?',
        answer:
          'BuilderLync is a web platform optimized for every device: desktop, tablet and phone. Open it in your phone or tablet browser in the field and you get the same CRM, estimates and job records as in the office, with nothing to install.',
      },
      {
        question: 'What integrations does BuilderLync offer?',
        answer:
          'BuilderLync natively integrates with QuickBooks, EagleView, ABC Supply, SRS Distribution, Google, and more — all included in your subscription.',
      },
    ],
  },

  proline: {
    slug: 'proline',
    name: 'Proline',
    company: 'Proline CRM',
    price: '$100–$300/mo',
    priceNote: 'Tiered pricing with feature gates',
    tagline: 'Mid-market CRM with limited AI and smaller integration ecosystem.',
    features: {
      allInOne: false,
      crm: true,
      aiLeadNurturing: 'limited',
      satelliteMeasurements: false,
      estimating: true,
      jobManagement: true,
      crewScheduling: 'limited',
      marketingAutomation: 'limited',
      weatherTracking: false,
      quickbooks: 'limited',
      documentSigning: false,
      unlimitedUsers: false,
      aiAgent: false,
      mobileApp: true,
    },
    pros: ['Decent pipeline management', 'Growing feature set'],
    cons: ['Limited AI/automation', 'Smaller ecosystem', 'No hail tracking'],
    whySwitch: [
      {
        title: 'Complete All-in-One Platform',
        description:
          'Proline is growing but still requires third-party tools for measurements, advanced marketing, and document signing. BuilderLync includes everything.',
      },
      {
        title: 'Real AI — Not Just Automation',
        description:
          'Proline offers basic automation. BuilderLync\'s AI agent Sierra can hold conversations, book appointments, and qualify leads autonomously.',
      },
      {
        title: 'Weather & Storm Tracking',
        description:
          'Proline has no hail or weather tracking. On the Pro plan, BuilderLync\'s Storm Intelligence shows you where storms hit so you can target neighborhoods with door-knocking campaigns.',
      },
    ],
    faqs: [
      {
        question: 'How does BuilderLync compare to Proline for CRM features?',
        answer:
          'Both offer pipeline management, but BuilderLync adds AI lead nurturing, satellite measurements, crew scheduling, and marketing automation — all in one platform.',
      },
      {
        question: 'Does BuilderLync offer e-signatures?',
        answer:
          'Yes. BuilderLync includes built-in document signing for proposals, contracts, and change orders — something Proline doesn\'t natively offer.',
      },
      {
        question: 'Can I switch from Proline to BuilderLync easily?',
        answer:
          'Yes. CSV imports are included on every plan. With our White Glove service, a paid add-on, our team handles the complete migration including data transfer, workflow setup, and team training.',
      },
      {
        question: 'Does BuilderLync work for solar contractors too?',
        answer:
          'BuilderLync is primarily built for roofing and exterior contractors, but our flexible workflow engine works great for solar companies as well.',
      },
      {
        question: 'What support does BuilderLync offer?',
        answer:
          'Starter includes email support. Pro adds priority chat and phone support, an onboarding session, and our AI assistant Sierra available 24/7. Enterprise adds a dedicated Customer Success Manager. White Glove setup and migration is available as a paid add-on.',
      },
    ],
  },

  rooflink: {
    slug: 'rooflink',
    name: 'RoofLink',
    company: 'RoofLink Software',
    price: '$100–$250/mo',
    priceNote: 'Basic pricing with limited feature depth',
    tagline: 'Basic roofing tool with limited features and no major integrations.',
    features: {
      allInOne: false,
      crm: 'limited',
      aiLeadNurturing: false,
      satelliteMeasurements: false,
      estimating: 'limited',
      jobManagement: 'limited',
      crewScheduling: false,
      marketingAutomation: false,
      weatherTracking: false,
      quickbooks: false,
      documentSigning: false,
      unlimitedUsers: false,
      aiAgent: false,
      mobileApp: 'limited',
    },
    pros: ['Simple interface', 'Low cost'],
    cons: ['Very limited features', 'No integrations', 'No AI'],
    whySwitch: [
      {
        title: 'From Basic to Best-in-Class',
        description:
          'RoofLink covers the basics but lacks the depth contractors need. BuilderLync gives you enterprise-grade tools at a fraction of the cost of piecing together multiple apps.',
      },
      {
        title: 'Integrations That Actually Work',
        description:
          'RoofLink has no major integrations. BuilderLync connects natively with QuickBooks, EagleView, ABC Supply, SRS Distribution, Google, and dozens more.',
      },
      {
        title: 'Grow Without Limits',
        description:
          'RoofLink limits users and features. BuilderLync gives you unlimited users and unlimited storage on every plan from day one.',
      },
    ],
    faqs: [
      {
        question: 'Is BuilderLync worth the higher price vs RoofLink?',
        answer:
          'Absolutely. BuilderLync replaces 8+ tools starting at a flat $497/mo, or $997/mo on Pro with Storm Intelligence and the AI stack. When you add up the cost of RoofLink plus the extra tools you need, BuilderLync is usually cheaper and far more capable.',
      },
      {
        question: 'Does BuilderLync work on phones and tablets?',
        answer:
          'BuilderLync is a web platform optimized for every device: desktop, tablet and phone. Open it in your phone or tablet browser in the field and you get the same CRM, estimates and job records as in the office, with nothing to install.',
      },
      {
        question: 'Can BuilderLync handle my estimating needs?',
        answer:
          'Yes. BuilderLync includes satellite-powered roof measurements, customizable estimate templates, material calculators, and e-signature proposals.',
      },
      {
        question: 'How long does migration take?',
        answer:
          'Most migrations from RoofLink are completed within 1–2 weeks. You can import by CSV yourself, or add our paid White Glove service and our team handles everything so your business never skips a beat.',
      },
      {
        question: 'What if my team isn\'t tech-savvy?',
        answer:
          'BuilderLync was built for the way roofing actually works. The interface is intuitive, and we provide hands-on training plus 24/7 support from our AI assistant Sierra.',
      },
    ],
  },
}

export const COMPETITOR_SLUGS = Object.keys(COMPETITORS)
